import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { inferLanguage } from "@/lib/parseBuildStream";

export type AiBuild = {
  id: string;
  user_id: string;
  slug: string;
  title: string;
  prompt: string;
  kind: "html" | "react";
  status: "draft" | "generating" | "ready" | "failed";
  visibility: "private" | "public";
  is_published: boolean;
  published_at: string | null;
  model: string;
  created_at: string;
  updated_at: string;
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40) || "site";

const randomSuffix = () => Math.random().toString(36).slice(2, 7);

export const useMyBuilds = () => {
  return useQuery({
    queryKey: ["ai-builds", "mine"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_builds")
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as AiBuild[];
    },
  });
};

export const useBuild = (buildId: string | null) => {
  return useQuery({
    queryKey: ["ai-build", buildId],
    enabled: !!buildId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_builds")
        .select("*")
        .eq("id", buildId!)
        .maybeSingle();
      if (error) throw error;
      return data as AiBuild | null;
    },
  });
};

export const useBuildFiles = (buildId: string | null) => {
  return useQuery({
    queryKey: ["ai-build-files", buildId],
    enabled: !!buildId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_build_files")
        .select("path, content, language")
        .eq("build_id", buildId!)
        .order("path");
      if (error) throw error;
      return (data ?? []) as { path: string; content: string; language: string }[];
    },
  });
};

export const useCreateBuild = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ prompt, title }: { prompt: string; title?: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Sign in to save builds");
      const t = (title || prompt.split("\n")[0] || "Untitled").slice(0, 60);
      const slug = `${slugify(t)}-${randomSuffix()}`;
      const { data, error } = await supabase
        .from("ai_builds")
        .insert({
          user_id: userData.user.id,
          slug,
          title: t,
          prompt,
          status: "generating",
        })
        .select()
        .single();
      if (error) throw error;
      return data as AiBuild;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-builds", "mine"] });
    },
  });
};

export const useSaveBuildResult = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      buildId: string;
      kind: "html" | "react";
      title: string;
      files: { path: string; content: string }[];
    }) => {
      const { buildId, kind, title, files } = args;
      // Replace files
      await supabase.from("ai_build_files").delete().eq("build_id", buildId);
      const rows = files.map((f) => ({
        build_id: buildId,
        path: f.path,
        content: f.content,
        language: inferLanguage(f.path),
        size_bytes: new TextEncoder().encode(f.content).length,
      }));
      if (rows.length > 0) {
        const { error } = await supabase.from("ai_build_files").insert(rows);
        if (error) throw error;
      }
      const { error: uErr } = await supabase
        .from("ai_builds")
        .update({ kind, title: title || undefined, status: "ready" })
        .eq("id", buildId);
      if (uErr) throw uErr;
      return { ok: true };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["ai-build", vars.buildId] });
      qc.invalidateQueries({ queryKey: ["ai-build-files", vars.buildId] });
      qc.invalidateQueries({ queryKey: ["ai-builds", "mine"] });
    },
  });
};

export const usePublishBuild = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      buildId,
      action,
      newSlug,
    }: {
      buildId: string;
      action?: "publish" | "unpublish";
      newSlug?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("ai-builder-publish", {
        body: {
          build_id: buildId,
          action: action ?? "publish",
          ...(newSlug ? { new_slug: newSlug } : {}),
        },
      });
      if (error) throw error;
      return data as { ok: boolean; published: boolean; url?: string; slug?: string };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["ai-build", vars.buildId] });
      qc.invalidateQueries({ queryKey: ["ai-builds", "mine"] });
    },
  });
};

export const useDeleteBuild = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (buildId: string) => {
      const { error } = await supabase.from("ai_builds").delete().eq("id", buildId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-builds", "mine"] }),
  });
};